import React, { useState, useEffect } from "react";
import { Shield, RefreshCw, Clock, AlertTriangle, AlertCircle, Key, Package } from "lucide-react";
import axios from "axios";
import { API_BASE_URL, API_ENDPOINTS, REQUEST_CONFIG } from "../config";

/**
 * CodeAnalysis component displays SAST (Static Application Security Testing) reports.
 * Shows security vulnerabilities found in the code.
 */
export default function CodeAnalysis() {
  const [sastReport, setSastReport] = useState(null);
  const [secretReport, setSecretReport] = useState(null);
  const [dependencyReport, setDependencyReport] = useState(null);
  const [sastReportLoading, setSastReportLoading] = useState(false);
  const [secretReportLoading, setSecretReportLoading] = useState(false);
  const [dependencyReportLoading, setDependencyReportLoading] = useState(false);
  
  // Fetch all reports when component mounts
  useEffect(() => {
    fetchSastReport();
    fetchSecretReport();
    fetchDependencyReport();
  }, []);
  
  // Function to fetch the SAST report
  const fetchSastReport = async () => {
    try {
      setSastReportLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.SAST_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        console.log('SAST report:', response.data);
        setSastReport(response.data);
      } else {
        console.error('Error fetching SAST report:', response.data);
        setSastReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch report'
        });
      }
      setSastReportLoading(false);
    } catch (error) {
      console.error('Error fetching SAST report:', error);
      setSastReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
      setSastReportLoading(false);
    }
  };
  
  // Function to fetch the Secret Detection report
  const fetchSecretReport = async () => {
    try {
      setSecretReportLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.SECRET_DETECTION_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        console.log('Secret Detection report:', response.data);
        setSecretReport(response.data);
      } else {
        console.error('Error fetching Secret Detection report:', response.data);
        setSecretReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch report'
        });
      }
      setSecretReportLoading(false);
    } catch (error) {
      console.error('Error fetching Secret Detection report:', error);
      setSecretReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
      setSecretReportLoading(false);
    }
  };
  
  // Function to fetch the Dependency Scanning report
  const fetchDependencyReport = async () => {
    try {
      setDependencyReportLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CODE.DEPENDENCY_SCANNING_REPORT}`,
        { headers: REQUEST_CONFIG.getAuthHeaders() }
      );
      
      if (response.data && response.data.status === 'success') {
        console.log('Dependency Scanning report:', response.data);
        setDependencyReport(response.data);
      } else {
        console.error('Error fetching Dependency Scanning report:', response.data);
        setDependencyReport({
          status: 'error',
          message: response.data.message || 'Failed to fetch report'
        });
      }
      setDependencyReportLoading(false);
    } catch (error) {
      console.error('Error fetching Dependency Scanning report:', error);
      setDependencyReport({
        status: 'error',
        message: error.message || 'An error occurred'
      });
      setDependencyReportLoading(false);
    }
  };
  
  // Helper function to format severity
  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critical':
      case 'high':
        return 'text-red-600 bg-red-50';
      case 'medium':
        return 'text-orange-600 bg-orange-50';
      case 'low':
        return 'text-yellow-600 bg-yellow-50';
      case 'info':
      case 'negligible':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };
  
  // Calculate report stats
  const calculateReportStats = (report) => {
    // Check if report exists and has the correct structure
    if (!report || !report.report) {
      return { total: 0, high: 0, medium: 0, low: 0, info: 0 };
    }
    
    // SAST reports have a different structure with a 'vulnerabilities' array
    const vulnerabilities = report.report.vulnerabilities || report.report;
    if (!Array.isArray(vulnerabilities)) {
      return { total: 0, high: 0, medium: 0, low: 0, info: 0 };
    }
    
    const stats = { total: vulnerabilities.length, high: 0, medium: 0, low: 0, info: 0 };
    
    vulnerabilities.forEach(vulnerability => {
      if (vulnerability.severity) {
        const severity = vulnerability.severity.toLowerCase();
        if (severity === 'critical' || severity === 'high') {
          stats.high++;
        } else if (severity === 'medium') {
          stats.medium++;
        } else if (severity === 'low') {
          stats.low++;
        } else {
          stats.info++;
        }
      }
    });
    
    return stats;
  };
  
  // Calculate stats for the reports
  const sastStats = calculateReportStats(sastReport);
  const secretStats = calculateReportStats(secretReport);
  const dependencyStats = calculateReportStats(dependencyReport);
  
  // Reusable vulnerability list component
  const renderVulnerabilityList = (report) => {
    if (!report || !report.report) {
      return <div className="px-4 py-3 text-center text-gray-500">No vulnerabilities found</div>;
    }
    
    const vulnerabilities = report.report.vulnerabilities || report.report;
    
    if (!Array.isArray(vulnerabilities) || vulnerabilities.length === 0) {
      return <div className="px-4 py-3 text-center text-gray-500">No vulnerabilities found</div>;
    }
    
    return vulnerabilities.map((vulnerability, index) => (
      <div key={index} className="px-4 py-3 hover:bg-gray-50">
        <div className="flex items-center mb-1">
          <AlertTriangle size={14} className="mr-1 text-yellow-500 flex-shrink-0" />
          <span className={`text-xs px-2 py-0.5 rounded-full ${getSeverityColor(vulnerability.severity)} flex-shrink-0`}>
            {vulnerability.severity}
          </span>
          <span className="text-xs text-gray-500 ml-2 overflow-hidden text-ellipsis">
            {vulnerability.name || vulnerability.check_name || (vulnerability.scanner && vulnerability.scanner.name) || 'Unknown'}
          </span>
        </div>
        <p className="text-sm font-medium text-gray-700">{vulnerability.message || vulnerability.description}</p>
        <div className="text-xs text-gray-500 mt-1 flex justify-between">
          <span>
            {vulnerability.location?.file || vulnerability.location?.path || vulnerability.file}
            {vulnerability.location?.start_line && `:${vulnerability.location.start_line}`}
            {vulnerability.location?.lines?.begin && `:${vulnerability.location.lines.begin}`}
          </span>
          {vulnerability.categories && (
            <span className="text-xs text-gray-400">
              {vulnerability.categories.join(', ')}
            </span>
          )}
          {vulnerability.identifiers && vulnerability.identifiers.some(id => id.type === 'cwe') && (
            <span className="text-xs text-gray-400">
              {vulnerability.identifiers.filter(id => id.type === 'cwe').map(id => `CWE-${id.value}`).join(', ')}
            </span>
          )}
        </div>
      </div>
    ));
  };
  
  return (
    <div className="flex flex-col h-full min-w-0 overflow-hidden">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold mb-4 text-gray-800">
          Code Security Analysis
        </h1>
      </div>
      
      <div className="space-y-6 overflow-auto pb-4 pr-2">
        {/* SAST Report Card */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 flex flex-col overflow-hidden" style={{ height: '480px', maxWidth: '100%' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-50 text-green-600 mr-4">
                <Shield size={24} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Security Vulnerabilities</h2>
                <p className="text-sm text-gray-500">SAST analysis</p>
              </div>
            </div>
            <button 
              onClick={fetchSastReport}
              disabled={sastReportLoading}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
              title="Refresh report"
            >
              <RefreshCw size={18} className={sastReportLoading ? "animate-spin" : ""} />
            </button>
          </div>
          
          {/* Report content */}
          {sastReportLoading ? (
            <div className="flex justify-center py-8 flex-grow">
              <div className="animate-pulse flex flex-col items-center">
                <div className="rounded-full bg-gray-200 w-12 h-12 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-24 mb-2.5"></div>
                <div className="h-3 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
          ) : !sastReport ? (
            <div className="text-center py-8 text-gray-500 flex-grow">
              No report available
            </div>
          ) : sastReport.status === 'error' ? (
            <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-red-700 flex-grow">
              <div className="flex mb-2">
                <AlertCircle size={18} className="mr-2 flex-shrink-0" />
                <h3 className="font-medium">Error retrieving report</h3>
              </div>
              <p className="text-sm">{sastReport.message}</p>
            </div>
          ) : (
            <div className="flex flex-col flex-grow min-w-0 overflow-hidden">
              {/* Report stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{sastStats.total}</div>
                  <div className="text-sm text-gray-500">Total Vulnerabilities</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-red-600">{sastStats.high}</div>
                  <div className="text-sm text-red-600">High</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-orange-600">{sastStats.medium}</div>
                  <div className="text-sm text-orange-600">Medium</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-yellow-600">{sastStats.low}</div>
                  <div className="text-sm text-yellow-600">Low</div>
                </div>
              </div>
              
              {/* Timestamp */}
              <div className="flex items-center text-sm text-gray-500 mb-4">
                <Clock size={14} className="mr-1" />
                Last updated: {sastReport.timestamp ? new Date(sastReport.timestamp).toLocaleString() : 'Unknown'}
              </div>
              
              {/* List of vulnerabilities */}
              <div className="border border-gray-100 rounded-lg overflow-hidden flex-grow flex flex-col">
                <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 border-b border-gray-100 sticky top-0">
                  Vulnerabilities List
                </div>
                <div className="divide-y divide-gray-100 overflow-y-auto overflow-x-auto flex-grow w-full min-w-0">
                  {renderVulnerabilityList(sastReport)}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Secret Detection Report Card */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 flex flex-col overflow-hidden" style={{ height: '480px', maxWidth: '100%' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-50 text-purple-600 mr-4">
                <Key size={24} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Secret Detection</h2>
                <p className="text-sm text-gray-500">Exposed credentials and tokens</p>
              </div>
            </div>
            <button 
              onClick={fetchSecretReport}
              disabled={secretReportLoading}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
              title="Refresh report"
            >
              <RefreshCw size={18} className={secretReportLoading ? "animate-spin" : ""} />
            </button>
          </div>
          
          {/* Report content */}
          {secretReportLoading ? (
            <div className="flex justify-center py-8 flex-grow">
              <div className="animate-pulse flex flex-col items-center">
                <div className="rounded-full bg-gray-200 w-12 h-12 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-24 mb-2.5"></div>
                <div className="h-3 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
          ) : !secretReport ? (
            <div className="text-center py-8 text-gray-500 flex-grow">
              No report available
            </div>
          ) : secretReport.status === 'error' ? (
            <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-red-700 flex-grow">
              <div className="flex mb-2">
                <AlertCircle size={18} className="mr-2 flex-shrink-0" />
                <h3 className="font-medium">Error retrieving report</h3>
              </div>
              <p className="text-sm">{secretReport.message}</p>
            </div>
          ) : (
            <div className="flex flex-col flex-grow min-w-0 overflow-hidden">
              {/* Report stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{secretStats.total}</div>
                  <div className="text-sm text-gray-500">Total Secrets</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-red-600">{secretStats.high}</div>
                  <div className="text-sm text-red-600">High</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-orange-600">{secretStats.medium}</div>
                  <div className="text-sm text-orange-600">Medium</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-yellow-600">{secretStats.low}</div>
                  <div className="text-sm text-yellow-600">Low</div>
                </div>
              </div>
              
              {/* Timestamp */}
              <div className="flex items-center text-sm text-gray-500 mb-4">
                <Clock size={14} className="mr-1" />
                Last updated: {secretReport.timestamp ? new Date(secretReport.timestamp).toLocaleString() : 'Unknown'}
              </div>
              
              {/* List of secrets */}
              <div className="border border-gray-100 rounded-lg overflow-hidden flex-grow flex flex-col">
                <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 border-b border-gray-100 sticky top-0">
                  Detected Secrets
                </div>
                <div className="divide-y divide-gray-100 overflow-y-auto overflow-x-auto flex-grow w-full min-w-0">
                  {renderVulnerabilityList(secretReport)}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Dependency Scanning Report Card */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 flex flex-col overflow-hidden" style={{ height: '480px', maxWidth: '100%' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-50 text-blue-600 mr-4">
                <Package size={24} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Dependency Vulnerabilities</h2>
                <p className="text-sm text-gray-500">Vulnerabilities in third-party packages</p>
              </div>
            </div>
            <button 
              onClick={fetchDependencyReport}
              disabled={dependencyReportLoading}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
              title="Refresh report"
            >
              <RefreshCw size={18} className={dependencyReportLoading ? "animate-spin" : ""} />
            </button>
          </div>
          
          {/* Report content */}
          {dependencyReportLoading ? (
            <div className="flex justify-center py-8 flex-grow">
              <div className="animate-pulse flex flex-col items-center">
                <div className="rounded-full bg-gray-200 w-12 h-12 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-24 mb-2.5"></div>
                <div className="h-3 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
          ) : !dependencyReport ? (
            <div className="text-center py-8 text-gray-500 flex-grow">
              No report available
            </div>
          ) : dependencyReport.status === 'error' ? (
            <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-red-700 flex-grow">
              <div className="flex mb-2">
                <AlertCircle size={18} className="mr-2 flex-shrink-0" />
                <h3 className="font-medium">Error retrieving report</h3>
              </div>
              <p className="text-sm">{dependencyReport.message}</p>
            </div>
          ) : (
            <div className="flex flex-col flex-grow min-w-0 overflow-hidden">
              {/* Report stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-800">{dependencyStats.total}</div>
                  <div className="text-sm text-gray-500">Total Vulnerabilities</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-red-600">{dependencyStats.high}</div>
                  <div className="text-sm text-red-600">High</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-orange-600">{dependencyStats.medium}</div>
                  <div className="text-sm text-orange-600">Medium</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-yellow-600">{dependencyStats.low}</div>
                  <div className="text-sm text-yellow-600">Low</div>
                </div>
              </div>
              
              {/* Timestamp */}
              <div className="flex items-center text-sm text-gray-500 mb-4">
                <Clock size={14} className="mr-1" />
                Last updated: {dependencyReport.timestamp ? new Date(dependencyReport.timestamp).toLocaleString() : 'Unknown'}
              </div>
              
              {/* List of vulnerabilities */}
              <div className="border border-gray-100 rounded-lg overflow-hidden flex-grow flex flex-col">
                <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 border-b border-gray-100 sticky top-0">
                  Vulnerable Dependencies
                </div>
                <div className="divide-y divide-gray-100 overflow-y-auto overflow-x-auto flex-grow w-full min-w-0">
                  {renderVulnerabilityList(dependencyReport)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
